import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AuthShell, GoogleButton, inputClass } from "@/pages/authShared";

export default function Register() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { loginWithToken } = useAuth();
  const navigate = useNavigate();

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 8 || !/[A-Za-z]/.test(form.password) || !/\d/.test(form.password)) {
      setError("Password must be at least 8 characters and include a letter and a number.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", form);
      loginWithToken(data.token, data.user);
      toast.success("Account created! Happy shopping 🛒");
      navigate("/market", { replace: true });
    } catch (err) {
      const msg = formatError(err.response?.data?.detail) || "Registration failed";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Create account" subtitle="Join Pepperonni Market today">
      <form onSubmit={submit} className="space-y-3">
        <input className={inputClass} placeholder="Full name" value={form.name} onChange={set("name")} data-testid="reg-name" required />
        <input className={inputClass} type="email" placeholder="Email" value={form.email} onChange={set("email")} data-testid="reg-email" required />
        <input className={inputClass} placeholder="Phone number" value={form.phone} onChange={set("phone")} data-testid="reg-phone" required />
        <input className={inputClass} type="password" placeholder="Password" value={form.password} onChange={set("password")} data-testid="reg-password" required />
        <ul className="space-y-1 px-1 text-xs" data-testid="password-hints">
          {[
            { ok: form.password.length >= 8, label: "At least 8 characters" },
            { ok: /[A-Za-z]/.test(form.password), label: "Contains a letter" },
            { ok: /\d/.test(form.password), label: "Contains a number" },
          ].map((r) => (
            <li key={r.label} className={`flex items-center gap-1.5 font-semibold ${r.ok ? "text-[#0B7A3B]" : "text-[#6B8375]"}`}>
              <span className="w-4 h-4 rounded-full flex items-center justify-center text-[9px] leading-none border" style={{ borderColor: r.ok ? "#0B7A3B" : "#6B8375" }}>{r.ok ? "✓" : ""}</span>
              {r.label}
            </li>
          ))}
        </ul>
        {error && (
          <div className="text-sm font-semibold text-[#C0392B] bg-[#C0392B]/10 rounded-[12px] px-3 py-2.5" data-testid="reg-error">{error}</div>
        )}
        <button
          type="submit"
          disabled={loading}
          data-testid="reg-submit"
          className="w-full bg-[#0B7A3B] text-white font-bold rounded-full py-3 active:scale-95 transition-transform hover:bg-[#095F2E] disabled:opacity-60"
        >
          {loading ? "Creating…" : "Create Account"}
        </button>
      </form>

      <div className="flex items-center gap-3 my-4">
        <div className="flex-1 h-px bg-[#F5F8F6]" />
        <span className="text-xs text-[#6B8375]">or</span>
        <div className="flex-1 h-px bg-[#F5F8F6]" />
      </div>
      <GoogleButton />

      <p className="text-center text-sm text-[#6B8375] mt-5">
        Already have an account?{" "}
        <Link to="/login" className="font-bold text-[#0B7A3B]" data-testid="go-login">
          Sign in
        </Link>
      </p>
    </AuthShell>
  );
}
